/**
 * The Hollow Barrow theme (+field): Mossvale's starter dungeon, a sunken shrine under Sunmeadow.
 * An enclosed crypt (cutaway walls, stone vaults, a root-hung cave mouth): cold mossy stone, roots
 * through the walls, rune-lit pillars and jambs in cool blue-green, warm braziers and candles at
 * the shrines. The King's tomb is a tall vaulted arena with a throne, rune pillars and four
 * braziers. Built by the generic themed builder (dungeon.ts) from the Venom Altar textures,
 * re-tinted cold, plus outdoor / venom procs; hero props in ./heroes.ts.
 */
import type { DungeonTheme, KitItem, PropKit, RGB, RoomStyle, WallItem } from '../types';

const stoneVar: readonly [RGB, RGB] = [
  [0.72, 0.78, 0.74],
  [0.88, 0.94, 0.9],
];
const boneVar: readonly [RGB, RGB] = [
  [0.82, 0.82, 0.76],
  [0.96, 0.95, 0.88],
];
const mossVar: readonly [RGB, RGB] = [
  [0.45, 0.58, 0.4],
  [0.6, 0.72, 0.5],
];
const coldVar: readonly [RGB, RGB] = [
  [0.7, 0.76, 0.74],
  [0.86, 0.9, 0.88],
];

const k = (asset: string, w: number, scale: readonly [number, number], r: number, h: number, extra: Partial<KitItem> = {}): KitItem => ({ asset, w, scale, r, h, ...extra });
const wi = (asset: string, w: number, scale: readonly [number, number], y: number, width: number, extra: Partial<WallItem> = {}): WallItem => ({ asset, w, scale, y, width, ...extra });

const BONES: KitItem[] = [
  k('kh_bone_A', 2, [1.2, 1.5], 0.4, 0.28, { tint: boneVar, detail: true, lift: 0.12 }),
  k('kh_bone_C', 2, [1.2, 1.5], 0.55, 0.28, { tint: boneVar, detail: true, lift: 0.12 }),
  k('kh_skull', 2, [1.1, 1.4], 0.46, 0.9, { tint: boneVar }),
  k('kh_ribcage', 1, [1.1, 1.3], 0.7, 0.9, { tint: boneVar }),
];
const CANDLES: KitItem[] = [
  k('kd_candle_triple', 2, [1.3, 1.6], 0.25, 0.87, { detail: true }),
  k('kd_candle_melted', 2, [1.3, 1.6], 0.17, 0.7, { detail: true }),
  k('kh_skull_candle', 1, [1.1, 1.3], 0.46, 1.19, {}),
];
const RUBBLE: KitItem[] = [
  k('kn_rock_largeA', 2, [3.0, 4.0], 0.45, 0.26, { tint: stoneVar }),
  k('kn_rock_smallA', 2, [3.0, 3.8], 0.18, 0.19, { tint: stoneVar, detail: true }),
  k('kn_statue_columnDamaged', 1, [3.4, 4.2], 0.18, 0.7, { tint: stoneVar }),
  k('kn_statue_block', 1, [3.0, 3.8], 0.24, 0.4, { tint: stoneVar }),
];
const GROWTH: KitItem[] = [
  k('kn_mushroom_tanGroup', 3, [2.6, 3.4], 0.14, 0.25, { detail: true }),
  k('kn_plant_flatShort', 1, [3.2, 4.0], 0.2, 0.2, { tint: mossVar, detail: true }),
  k('out:roots', 2, [0.9, 1.3], 0.6, 2.0, {}),
  k('out:crystals', 1, [0.55, 0.8], 0.5, 1.4, {}),
];
const DEBRIS: KitItem[] = [
  k('kn_rock_smallFlatA', 3, [3.0, 4.0], 0.25, 0.07, { tint: stoneVar, detail: true }),
  k('kh_bone_A', 2, [1.1, 1.4], 0.4, 0.28, { tint: boneVar, detail: true, lift: 0.12 }),
  k('kh_bone_C', 1, [1.1, 1.4], 0.55, 0.28, { tint: boneVar, detail: true, lift: 0.12 }),
  k('proc:rubble', 2, [0.9, 1.2], 0.75, 0.6, { tint: coldVar, detail: true }),
  k('kn_mushroom_tanGroup', 1, [2.2, 2.8], 0.14, 0.25, { detail: true }),
];
const MOSS_HANG: WallItem[] = [wi('kn_hanging_moss', 3, [3.2, 4.2], 2.4, 0.8, { off: -0.1, tint: [mossVar[0], mossVar[1]], detail: true })];
const GRAVE_BANNERS: WallItem[] = [wi('kd_banner_thin_green', 2, [1, 1.1], 0, 1.1, { off: 0.5, farOnly: true, tint: [coldVar[0], coldVar[1]] })];

const kit = (p: Partial<PropKit> & Pick<PropKit, 'clutter'>): PropKit => ({
  debris: DEBRIS,
  islands: 1.6,
  cluster: [2, 4],
  spacing: 4.4,
  wall: [...MOSS_HANG, ...GRAVE_BANNERS],
  wallSpacing: 6.5,
  foliage: GROWTH,
  foliageChance: 0.5,
  vines: 0.55,
  torchSpacing: 13,
  decals: 0.6,
  ...p,
});

export const BARROW_KITS: Record<string, PropKit> = {
  mouth: kit({ clutter: [...RUBBLE, ...GROWTH, k('proc:urn', 1, [1.0, 1.2], 0.42, 0.97, { tint: coldVar })], foliageChance: 0.8, vines: 0.85, torchSpacing: 0 }),
  ossuary: kit({ clutter: [...BONES, ...BONES, ...CANDLES, k('proc:urn', 1, [1.0, 1.2], 0.42, 0.97, { tint: coldVar }), k('kh_post_skull', 1, [1.2, 1.4], 0.6, 3.3, {})], spacing: 3.8 }),
  crypt: kit({ clutter: [...CANDLES, ...CANDLES, k('proc:urn', 2, [1.0, 1.25], 0.42, 0.97, { tint: coldVar }), k('kh_shrine_candles', 1, [1.2, 1.4], 0.6, 1.79, {}), ...BONES], wall: [...GRAVE_BANNERS, wi('kd_shelf_small_candles', 2, [1.3, 1.5], 1.5, 1.2, { off: 0 }), ...MOSS_HANG] }),
  shrine: kit({ clutter: [...CANDLES, k('kh_shrine_candles', 2, [1.2, 1.4], 0.6, 1.79, {}), k('out:crystals', 2, [0.7, 0.95], 0.5, 1.6, {}), ...BONES], cluster: [2, 3], spacing: 5, torchSpacing: 0 }),
  tomb: kit({ clutter: [...BONES, k('kh_post_skull', 1, [1.2, 1.4], 0.6, 3.3, {}), k('out:crystals', 2, [0.8, 1.1], 0.5, 1.8, {}), ...CANDLES, ...RUBBLE], cluster: [2, 3], spacing: 5.2, torchSpacing: 0 }),
  corridor: kit({ clutter: [...CANDLES, k('kh_skull', 1, [1.0, 1.2], 0.46, 0.9, { tint: boneVar, detail: true })], cluster: [1, 2], spacing: 6, foliageChance: 0.45 }),
};

const ROOMS: Record<string, RoomStyle> = {
  barrow_mouth: { archetype: 'mouth', floor: 'dirt', light: 'warm', path: 'flag', wallH: 7, ceiling: 'cave' },
  ossuary: { archetype: 'ossuary', floor: 'flag', light: 'jade', border: 'tiles', wallH: 8, ceiling: 'vault' },
  lantern_crypt: { archetype: 'crypt', floor: 'tiles', light: 'jade', path: 'flag', border: 'flag', wallH: 8, ceiling: 'vault' },
  bonecaller_shrine: { archetype: 'shrine', floor: 'tiles', margin: 3.5, light: 'warm', border: 'flag', wallH: 9, ceiling: 'vault' },
  bone_gallery: { archetype: 'ossuary', floor: 'dirt', light: 'jade', path: 'flag', wallH: 7, ceiling: 'cave' },
  kings_tomb: { archetype: 'tomb', floor: 'tiles', margin: 4, light: 'warm', border: 'flag', wallH: 11, ceiling: 'vault' },
};

export const hollowBarrowTheme: DungeonTheme = {
  id: 'hollow_barrow',
  name: 'The Hollow Barrow',
  floors: {
    flag: { texture: 'venom_floor_flagstone', tint: 0xd6e2da, tileYd: 6.5 },
    dirt: { texture: 'venom_floor_dirt_roots', tint: 0xdcdcd0, tileYd: 6, base: 0.95 },
    tiles: { texture: 'venom_floor_temple_tiles', tint: 0xcfdcd6, tileYd: 9 },
  },
  surfaces: {
    ledgeTop: { texture: 'venom_floor_temple_tiles', tint: 0xa8b8ae, tileYd: 6 },
    stone: { texture: 'venom_wall_sandstone', tint: 0xa6b6b0, tileYd: 4 },
    cliff: { texture: 'venom_wall_sandstone', tint: 0x6e7a74, tileYd: 4 },
    trim: { tint: 0xffffff, tileYd: 2.4 },
    wood: { texture: 'venom_wood_planks', tint: 0xa8a49a, tileYd: 3 },
  },
  arch: {
    ledgeHeight: 0.6,
    roomMargin: 2.8,
    corridorMargin: 1.1,
    wallThickness: 2.4,
    wallHeights: { far: 4, near: 1.6, side: 2.8 },
    baseY: -2.5,
    trim: false,
    enclosed: { roomWallH: 8, corridorWallH: 5, cutHeight: 1.7, vaultRise: 0.34 },
  },
  room: (r) => ROOMS[r.id] ?? { archetype: 'crypt', floor: 'flag', light: 'jade' },
  corridorFloor: (c) => (c.id === 'c_mouth_ossuary' ? 'dirt' : 'flag'),
  corridorStyle: (c) => (c.id === 'c_mouth_ossuary' ? { wallH: 5.5, ceiling: 'cave' } : { wallH: 5, ceiling: 'vault' }),
  kits: BARROW_KITS,
  heroes: {
    barrow_arch: { half: [4.2, 1.0], h: 5.9 },
    crypt_arch: { half: [1, 1], h: 5.9, opening: true },
    sarcophagus: { half: [1.9, 1.0], h: 1.5, light: 'jade' },
    bone_pile: { half: [1.6, 1.2], h: 1.0 },
    skull_mound: { half: [1.6, 1.4], h: 1.6 },
    candles: { half: [1.0, 1.0], h: 1.2, light: 'warm' },
    urn: { half: [1.2, 0.8], h: 1.1 },
    lantern_plinth: { half: [1.1, 1.1], h: 4.0, light: 'jade' },
    grave_banner: { half: [0.8, 0.5], h: 3.2 },
    root_tangle: { half: [1.4, 1.0], h: 3.2 },
    bone_pillar: { half: [1.2, 1.2], h: 6, light: 'jade' },
    tomb_pillar: { half: [1.2, 1.2], h: 7.5, light: 'jade' },
    barrow_throne: { half: [3.2, 2.0], h: 4.6, light: 'jade' },
    altar_dais: { half: [3.4, 1.8], h: 2.2, light: 'warm' },
    brazier: { half: [0.9, 0.9], h: 2, flame: true, light: 'warm' },
    cobweb: { half: [0.8, 0.5], h: 2.8 },
    floor_rune: { half: [1, 1], h: 0, floorDecal: true },
  },
  // Daylight falls in through the barrow mouth only.
  shafts: { barrow_mouth: 2 },
  lighting: {
    background: 0x04080a,
    fog: [0.035, 0.07, 0.075],
    hemiSky: 0xbcdce2,
    hemiGround: 0x2a3430,
    hemiIntensity: 0.52,
    key: 0xe6f2ff,
    lift: [0.005, 0.012, 0.016],
  },
  // Warm braziers / candles; the "jade" slot is the barrow's cold rune light.
  glow: { warm: [1.0, 0.55, 0.22], jade: [0.2, 0.8, 0.78] },
};
